import React from 'react';

import {CpcOutlook, GefsOutlook} from 'src/lib/api/regionalReport';

import AnomalyGauge from './AnomalyGauge';
import {GEFS_FORECAST_NAME} from './commentary';

function meanDefined(vals: (number | null)[]): number | null {
  const clean = vals.filter((v): v is number => v != null && isFinite(v));
  if (clean.length === 0) return null;
  return clean.reduce((a, b) => a + b, 0) / clean.length;
}

function p90AbsFloor(vals: (number | null)[], floor: number): number {
  const absVals = vals
    .filter((v): v is number => v != null && isFinite(v))
    .map((v) => Math.abs(v))
    .sort((a, b) => a - b);
  if (absVals.length === 0) return floor;
  const idx = Math.floor(absVals.length * 0.9);
  return Math.max(floor, absVals[Math.min(idx, absVals.length - 1)]);
}

type CpcSide = 'temperature' | 'precipitation';

function pickCpcDominant(cat: {aboveNormal: number; nearNormal: number; belowNormal: number}) {
  const max = Math.max(cat.aboveNormal, cat.nearNormal, cat.belowNormal);
  let label = 'Near-normal';
  if (max === cat.aboveNormal && cat.aboveNormal > cat.nearNormal) label = 'Above-normal';
  else if (max === cat.belowNormal && cat.belowNormal > cat.nearNormal) label = 'Below-normal';
  return {label, pct: max};
}

const US_DROUGHT_MONITOR_URL =
  'https://droughtmonitor.unl.edu/CurrentMap/StateDroughtMonitor.aspx?West';

function DroughtMonitorNote() {
  return (
    <div className="text-xs text-gray-400 italic">
      For current drought conditions, see the{' '}
      <a
        href={US_DROUGHT_MONITOR_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        US Drought Monitor
      </a>
      .
    </div>
  );
}

/**
 * Compact trio of anomaly gauges for the Exec Summary row:
 *   1. GEFS precipitation anomaly over the 35-day horizon
 *   2. GEFS temperature anomaly over the 35-day horizon
 *   3. CPC 8-14 day dominant-category probability (if available)
 * Falls back gracefully when either data source is null.
 */
export default function GefsOutlookGauges({
  gefs,
  cpc,
}: {
  gefs: GefsOutlook | null;
  cpc: CpcOutlook | null;
}) {
  const precipMean = gefs ? meanDefined(gefs.precipitation.anomaly) : null;
  const tempMean = gefs ? meanDefined(gefs.temperature.anomaly) : null;
  const precipBound = gefs ? p90AbsFloor(gefs.precipitation.anomaly, 0.05) : 0.05;
  const tempBound = gefs ? p90AbsFloor(gefs.temperature.anomaly, 2) : 2;

  const cpcSide: CpcSide | null = (() => {
    if (!cpc) return null;
    if (cpc.precipitation) return 'precipitation';
    if (cpc.temperature) return 'temperature';
    return null;
  })();
  const cpcDominant = cpc && cpcSide ? pickCpcDominant(cpc[cpcSide]!) : null;

  const anyGauge = precipMean != null || tempMean != null || cpcDominant != null;
  if (!anyGauge) {
    // The executive summary already cites the CPC outlook whenever it has an
    // issue date, so only call the outlooks unavailable when it has none.
    if (cpc?.issueDate) return <DroughtMonitorNote />;
    // No external outlooks available (GEFS + CPC + NIDIS all null). Render a
    // small notice so reviewers know the section exists but is empty — better
    // than silently vanishing.
    return (
      <div className="text-xs text-gray-400 italic">
        External outlooks ({GEFS_FORECAST_NAME}; NOAA CPC 8–14 day) unavailable for this report. See{' '}
        <a
          href="https://www.cpc.ncep.noaa.gov/products/predictions/814day/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          NOAA CPC
        </a>
        {' · '}
        <a
          href={US_DROUGHT_MONITOR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          US Drought Monitor
        </a>
        .
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start gap-3 flex-wrap">
        {precipMean != null && gefs && (
          <AnomalyGauge
            value={precipMean}
            bound={precipBound}
            unit={gefs.precipitation.unit}
            label={`${GEFS_FORECAST_NAME}: precip`}
            palette="precip"
          />
        )}
        {tempMean != null && gefs && (
          <AnomalyGauge
            value={tempMean}
            bound={tempBound}
            unit={gefs.temperature.unit}
            label={`${GEFS_FORECAST_NAME}: temp`}
            palette="temperature"
          />
        )}
        {cpcDominant && (
          <div className="flex flex-col items-center justify-center min-w-[140px] px-3 py-2 border border-gray-200 rounded-sm bg-gray-50">
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">
              CPC 8–14 day {cpcSide}
            </div>
            <div className="text-sm font-semibold mt-0.5">{cpcDominant.label}</div>
            <div className="text-xs text-gray-600 tabular-nums">
              {Math.round(cpcDominant.pct)}% probability
            </div>
            {cpc?.validPeriod && (
              <div className="text-[9px] text-gray-400 italic mt-0.5">{cpc.validPeriod}</div>
            )}
          </div>
        )}
      </div>
      <div className="mt-2">
        <DroughtMonitorNote />
      </div>
    </div>
  );
}
